/**
 * TrailSystem — pooled position-history ribbons + dash-afterimage ghost
 * sprites (trails-and-afterimages spec §4/§6).
 *
 * The one new motion piece of the trail grammar: no existing motion
 * primitive stores history (FollowerSystem snaps ONE object to the live
 * anchor each frame), and the status-quo dash pattern (a full def playback
 * every 40ms) applied to projectiles would saturate the 30-graphics budget
 * with five projectiles (spec §4.3). A ribbon here is ONE ribbon view per
 * fed entity (segment sprites, utils/RibbonView), re-posed each frame from a
 * preallocated ring buffer — transforms only, never a Graphics redraw, which
 * rebuilt the whole Pixi stage every frame (plan 16 §2.4).
 *
 * Position OWNERS feed it (ProjectileRenderer.update — projectiles are not
 * resolvable entity ids in the VFXEffectPlayer resolver); VFXEffectPlayer
 * routes `type:'ribbon'` primitives to attach() and pumps update().
 *
 * Lifecycle naming (attach/release/tick) mirrors helix-core
 * helix-vfx-trails' EmitterController start/stop/tick for the lift
 * (spec §1.7). Module singleton (CooldownStore pattern) so position owners
 * (rendering), the dash pipeline, and the effect player can all reach it
 * without ServiceRegistry plumbing or cycles.
 *
 * Pooling / degrade rules (§6): ring buffers + ribbon views + ghost sprites
 * are all pooled — steady state allocates nothing. Pooled views and expired
 * ghosts are hidden by alpha, never `visible` (a structural change in Pixi 8).
 * Refusals (trail cap, graphics budget, ghost pool) are counted in
 * getStats(), never silent.
 */

import type { ISprite, SpriteOptions } from '../../rendering/IRenderer';
import type { VFXPrimitive } from '../VFXEffectRegistry';
import { createRibbonView, type IRibbonView, type RibbonViewStyle } from '../../rendering/utils/RibbonView';
import { createSprite } from '../../rendering/utils/Sprite';
import { vfxBudgetManager } from '../VFXBudgetManager';
import { TRAIL_FX_CONFIG } from '../../../config/abilities/trailFx.config';
import { TrailStore } from '../../diagnostics/TrailStore';
import { VisualLifecycleStore } from '../../diagnostics/VisualLifecycleStore';
import { gameLogger } from '../../../utils/GameLogger';

/** Injectable surface for unit tests (no Pixi factories in Jest). */
export interface TrailFactories {
  createRibbonView(scene: unknown, style: RibbonViewStyle): IRibbonView;
  createSprite(scene: unknown, opts: SpriteOptions): ISprite;
}

interface RibbonStyle {
  color: number;
  glowColor: number;
  width: number;
  lengthMs: number;
  maxPoints: number;
  taper: boolean;
  fade: boolean;
  glow: boolean;
  depth: number;
  alpha: number;
}

interface RibbonEntry {
  key: string;
  abilityId: string | null;
  gfx: IRibbonView;
  /** Ring of [x, y, ts] triplets, capacity style.maxPoints. */
  buf: Float64Array;
  /** Next write slot (0..maxPoints-1). */
  head: number;
  /** Live samples. */
  count: number;
  style: RibbonStyle;
  /** Feeding stopped — drain naturally, then recycle. */
  released: boolean;
  /** A collapse after a hit: the tail catches the head over drainMs from releasedAt. */
  collapse?: { releasedAt: number; drainMs: number };
  lastSampleTs: number;
}

export interface GhostSpawnOpts {
  x: number;
  y: number;
  /** Player texture key — must already be loaded (the live sprite's key). */
  textureKey: string;
  tint?: number;
  lifeMs?: number;
  startAlpha?: number;
  blend?: 'add' | 'normal';
  flipX?: boolean;
  sizePx?: number;
  abilityId?: string;
}

interface GhostSlot {
  sprite: ISprite;
  active: boolean;
  /** Last texture / blend set — only a change is written (each is a stage rebuild). */
  textureKey: string;
  blend: 'add' | 'normal' | null;
  spawnTs: number;
  lifeMs: number;
  startAlpha: number;
}

export class TrailSystemImpl {
  private scene: unknown = null;
  private ribbons = new Map<string, RibbonEntry>();
  /** Recycled ring buffers (all default-cap; reused when large enough). */
  private bufferPool: Float64Array[] = [];
  /** Recycled ribbon views keyed by `${depth}:${glow}` (depth/glow-layer
   *  are fixed at creation). */
  private gfxPool = new Map<string, IRibbonView[]>();
  private ghosts: GhostSlot[] = [];
  /** Reusable point objects + polyline scratch — zero alloc per redraw. */
  private pointPool: Array<{ x: number; y: number }> = [];
  private polyScratch: Array<{ x: number; y: number }> = [];
  private refusals = { trailCap: 0, graphicsBudget: 0, ghostPool: 0 };

  constructor(private factories?: Partial<TrailFactories>) {}

  /** Swap scenes: every live ribbon/ghost belongs to the old scene — drop all. */
  public setScene(scene: unknown): void {
    this.clear();
    this.scene = scene;
  }

  /**
   * Register a ribbon under a feed key (projectile/entity id). Consults the
   * cap + graphics budget ONCE (never retried mid-flight — refusal means the
   * projectile keeps its painted body smear, the zero-cost floor §1.1).
   */
  public attach(key: string, prim: VFXPrimitive, meta?: { abilityId?: string }): boolean {
    if (!this.scene || !key || this.ribbons.has(key)) return false;

    if (this.ribbons.size >= TRAIL_FX_CONFIG.maxActiveTrails) {
      this.refusals.trailCap++;
      gameLogger.debug('vfx', `[TrailSystem] trail cap reached, refusing ${key}`);
      return false;
    }
    const stats = vfxBudgetManager.getStats();
    if (
      !vfxBudgetManager.canSpawnGraphics()
      || stats.activeGraphics >= stats.maxGraphics * TRAIL_FX_CONFIG.graphicsBudgetRefuseFraction
    ) {
      this.refusals.graphicsBudget++;
      gameLogger.debug('vfx', `[TrailSystem] graphics budget high, refusing ${key}`);
      return false;
    }

    const r = TRAIL_FX_CONFIG.ribbon;
    const style: RibbonStyle = {
      color: prim.color ?? 0xffffff,
      glowColor: prim.ribbonGlowColor ?? 0xffffff,
      width: prim.width ?? r.width,
      lengthMs: prim.ribbonLengthMs ?? r.lengthMs,
      maxPoints: Math.max(2, prim.ribbonMaxPoints ?? r.maxPoints),
      taper: prim.ribbonTaper ?? true,
      fade: prim.ribbonFade ?? true,
      glow: prim.glow ?? false,
      depth: prim.depth ?? r.depth,
      alpha: prim.alpha ?? r.alpha,
    };

    const gfx = this.acquireGraphics(style);
    if (!gfx) return false;

    const entry: RibbonEntry = {
      key,
      abilityId: meta?.abilityId ?? null,
      gfx,
      buf: this.acquireBuffer(style.maxPoints),
      head: 0,
      count: 0,
      style,
      released: false,
      lastSampleTs: 0,
    };
    this.ribbons.set(key, entry);

    TrailStore.record({
      ts: Date.now(), kind: 'trail_attach', abilityId: entry.abilityId, key,
      headX: 0, headY: 0, tailX: 0, tailY: 0, pointCount: 0,
    });
    VisualLifecycleStore.record({
      type: 'trail_vfx',
      label: `${entry.abilityId ?? '?'} ribbon attach (${key})`,
      x: 0, y: 0,
      abilityId: entry.abilityId,
      meta: { key, phase: 'attach' },
    });
    return true;
  }

  /** Push a world-space sample. Fed by position owners; O(1) ring write. */
  public feed(key: string, x: number, y: number, now?: number): void {
    const e = this.ribbons.get(key);
    if (!e || e.released) return;
    const ts = now ?? Date.now();
    const i = e.head * 3;
    e.buf[i] = x;
    e.buf[i + 1] = y;
    e.buf[i + 2] = ts;
    e.head = (e.head + 1) % e.style.maxPoints;
    if (e.count < e.style.maxPoints) e.count++;
  }

  /**
   * Stop feeding; the ribbon drains naturally (samples expire over
   * lengthMs), then its graphic + buffer recycle to the pools. Idempotent.
   * `collapse` (a projectile hit): feed the contact point, then pull the tail
   * into it over `drainMs` instead of leaving the streak to fade in place.
   */
  public release(key: string, collapse?: { at: { x: number; y: number }; drainMs: number; now?: number }): void {
    const e = this.ribbons.get(key);
    if (!e || e.released) return;
    if (collapse) {
      const now = collapse.now ?? Date.now();
      this.feed(key, collapse.at.x, collapse.at.y, now);
      e.collapse = { releasedAt: now, drainMs: Math.max(1, collapse.drainMs) };
    }
    e.released = true;
    const head = this.pointAt(e, e.count - 1);
    const tail = this.pointAt(e, 0);
    TrailStore.record({
      ts: Date.now(), kind: 'trail_release', abilityId: e.abilityId, key,
      headX: head?.x ?? 0, headY: head?.y ?? 0,
      tailX: tail?.x ?? 0, tailY: tail?.y ?? 0,
      pointCount: e.count,
    });
    VisualLifecycleStore.record({
      type: 'trail_vfx',
      label: `${e.abilityId ?? '?'} ribbon release (${key})`,
      x: head?.x ?? 0, y: head?.y ?? 0,
      abilityId: e.abilityId,
      meta: { key, phase: 'release' },
    });
  }

  /** Per-frame pump — expire history, redraw ribbons, decay ghosts. */
  public update(now?: number): void {
    const ts = now ?? Date.now();

    for (const e of this.ribbons.values()) {
      this.expire(e, ts);
      if (e.released && e.count === 0) {
        this.recycle(e);
        continue;
      }
      this.redraw(e);
      if (e.count > 0 && ts - e.lastSampleTs >= TRAIL_FX_CONFIG.sampleIntervalMs) {
        e.lastSampleTs = ts;
        const head = this.pointAt(e, e.count - 1)!;
        const tail = this.pointAt(e, 0)!;
        TrailStore.record({
          ts: Date.now(), kind: 'trail_sample', abilityId: e.abilityId, key: e.key,
          headX: head.x, headY: head.y, tailX: tail.x, tailY: tail.y,
          pointCount: e.count,
        });
      }
    }

    for (const g of this.ghosts) {
      if (!g.active) continue;
      const t = (ts - g.spawnTs) / g.lifeMs;
      if (t >= 1) {
        g.active = false;
        g.sprite.setAlpha(0);
      } else {
        g.sprite.setAlpha(g.startAlpha * (1 - t));
      }
    }
  }

  /**
   * Spawn one afterimage sprite from a pooled slot. Pool-exhausted → skip
   * this emission (degrade rule 3) and count the refusal. Returns success.
   */
  public spawnGhost(opts: GhostSpawnOpts): boolean {
    if (!this.scene || !opts.textureKey) return false;
    const g = TRAIL_FX_CONFIG.ghost;
    let slot = this.ghosts.find((s) => !s.active);
    if (!slot) {
      if (this.ghosts.length >= TRAIL_FX_CONFIG.maxGhosts) {
        this.refusals.ghostPool++;
        return false;
      }
      const sizePx = opts.sizePx ?? g.sizePx;
      const factory = this.factories?.createSprite ?? createSprite;
      let sprite: ISprite;
      try {
        sprite = factory(this.scene, {
          x: opts.x, y: opts.y,
          initialTextureKey: opts.textureKey,
          width: sizePx, height: sizePx,
          depth: g.depth,
        });
      } catch {
        return false; // Pixi factory not registered (headless) — degrade silently
      }
      slot = {
        sprite, active: false, spawnTs: 0, lifeMs: g.lifeMs, startAlpha: g.startAlpha,
        textureKey: opts.textureKey, blend: null,
      };
      this.ghosts.push(slot);
    }

    const sizePx = opts.sizePx ?? g.sizePx;
    const sprite = slot.sprite;
    // A texture or blend change is a stage rebuild: write only a change.
    if (slot.textureKey !== opts.textureKey) {
      sprite.setTexture(opts.textureKey);
      slot.textureKey = opts.textureKey;
    }
    const blend = opts.blend ?? 'add';
    if (slot.blend !== blend) {
      sprite.setBlendMode(blend);
      slot.blend = blend;
    }
    sprite.setPosition(opts.x, opts.y);
    sprite.setDisplaySize(sizePx, sizePx);
    sprite.setTint(opts.tint ?? g.tint);
    sprite.setFlipX(opts.flipX === true);
    sprite.setAlpha(opts.startAlpha ?? g.startAlpha);
    slot.active = true;
    slot.spawnTs = Date.now();
    slot.lifeMs = opts.lifeMs ?? g.lifeMs;
    slot.startAlpha = opts.startAlpha ?? g.startAlpha;

    TrailStore.record({
      ts: Date.now(), kind: 'ghost_spawn', abilityId: opts.abilityId ?? null, key: null,
      headX: opts.x, headY: opts.y, tailX: opts.x, tailY: opts.y, pointCount: 1,
    });
    return true;
  }

  /** Observability — active counts, pool sizes, refusal counters (§6). */
  public getStats() {
    return {
      activeTrails: this.ribbons.size,
      pooledBuffers: this.bufferPool.length,
      pooledGraphics: Array.from(this.gfxPool.values()).reduce((n, a) => n + a.length, 0),
      ghostPoolSize: this.ghosts.length,
      activeGhosts: this.ghosts.filter((g) => g.active).length,
      refusals: { ...this.refusals },
    };
  }

  /** Live ribbons with their sample counts (render-state truth for QA). */
  public getRibbonRows(): Array<{ key: string; abilityId: string | null; count: number; released: boolean }> {
    return Array.from(this.ribbons.values(), (e) => ({ key: e.key, abilityId: e.abilityId, count: e.count, released: e.released }));
  }

  /** Destroy everything (scene swap / teardown). Pools drop too — pooled
   *  display objects belong to the outgoing scene. */
  public clear(): void {
    for (const e of this.ribbons.values()) {
      e.gfx.destroy();
      vfxBudgetManager.onShapeDestroyed(true);
    }
    this.ribbons.clear();
    for (const list of this.gfxPool.values()) list.forEach((g) => g.destroy());
    this.gfxPool.clear();
    this.bufferPool.length = 0;
    for (const g of this.ghosts) g.sprite.destroy();
    this.ghosts.length = 0;
  }

  // ── internals ─────────────────────────────────────────────────────

  /** Logical index i (0 = oldest) → world point, or null when empty. */
  private pointAt(e: RibbonEntry, i: number): { x: number; y: number } | null {
    if (i < 0 || i >= e.count) return null;
    const cap = e.style.maxPoints;
    const slot = ((e.head - e.count + i) % cap + cap) % cap;
    return { x: e.buf[slot * 3], y: e.buf[slot * 3 + 1] };
  }

  /** Drop samples older than lengthMs (advance the logical tail). */
  private expire(e: RibbonEntry, now: number): void {
    const cap = e.style.maxPoints;
    // Oldest sample time kept: the normal window, or a collapse's cut-off sweeping
    // from (release − lengthMs) to the release over drainMs, then everything.
    let keepFrom = now - e.style.lengthMs;
    if (e.collapse) {
      const { releasedAt, drainMs } = e.collapse;
      const progress = (now - releasedAt) / drainMs;
      keepFrom = progress >= 1 ? Infinity : Math.max(keepFrom, releasedAt - e.style.lengthMs * (1 - progress));
    }
    while (e.count > 0) {
      const tailSlot = ((e.head - e.count) % cap + cap) % cap;
      if (e.buf[tailSlot * 3 + 2] >= keepFrom) break;
      e.count--;
    }
  }

  /** Re-pose the ribbon's segments from the ring history (world coords —
   *  the history owns all motion; no tween may target a ribbon). */
  private redraw(e: RibbonEntry): void {
    const view = e.gfx;
    view.begin();
    if (e.count < 2) {
      view.end();
      return;
    }

    const cap = e.style.maxPoints;
    // Fill the reusable scratch with pooled point objects — no allocation
    // after warmup (the K15 lesson: no per-frame churn in render paths).
    while (this.pointPool.length < e.count) this.pointPool.push({ x: 0, y: 0 });
    this.polyScratch.length = 0;
    for (let i = 0; i < e.count; i++) {
      const slot = ((e.head - e.count + i) % cap + cap) % cap;
      const p = this.pointPool[i];
      p.x = e.buf[slot * 3];
      p.y = e.buf[slot * 3 + 1];
      this.polyScratch.push(p);
    }

    const s = e.style;
    const pts = this.polyScratch;
    const segs = e.count - 1;
    // Glow underlay: wide soft segments (the beam primitive's layered-stroke
    // family), then the core stepped in width/alpha toward the tail.
    // Each interior joint gets a round cap at the wider (head-side) segment's
    // width, so bends read as one stroke.
    for (let i = 0; i < segs; i++) {
      view.segment('glow', pts[i].x, pts[i].y, pts[i + 1].x, pts[i + 1].y, s.width * 2.5, s.glowColor, s.alpha * 0.3);
      if (i > 0) view.joint('glow', pts[i].x, pts[i].y, s.width * 2.5, s.glowColor, s.alpha * 0.3);
    }
    for (let i = 0; i < segs; i++) {
      const f = (i + 1) / segs; // 0→tail, 1→head
      const w = s.taper ? Math.max(1, s.width * f) : s.width;
      const a = s.fade ? s.alpha * (0.15 + 0.85 * f) : s.alpha;
      view.segment('core', pts[i].x, pts[i].y, pts[i + 1].x, pts[i + 1].y, w, s.color, a);
      if (i > 0) view.joint('core', pts[i].x, pts[i].y, w, s.color, a);
    }
    view.end();
  }

  private acquireBuffer(maxPoints: number): Float64Array {
    const need = maxPoints * 3;
    const idx = this.bufferPool.findIndex((b) => b.length >= need);
    if (idx >= 0) return this.bufferPool.splice(idx, 1)[0];
    return new Float64Array(need);
  }

  private acquireGraphics(style: RibbonStyle): IRibbonView | null {
    const poolKey = `${style.depth}:${style.glow ? 1 : 0}`;
    const pooled = this.gfxPool.get(poolKey)?.pop();
    if (pooled) {
      pooled.unpark();
      vfxBudgetManager.onShapeCreated(true);
      return pooled;
    }
    const factory = this.factories?.createRibbonView ?? createRibbonView;
    try {
      const view = factory(this.scene, { depth: style.depth, glow: style.glow });
      vfxBudgetManager.onShapeCreated(true);
      return view;
    } catch {
      return null; // ribbon factory unavailable (headless) — degrade
    }
  }

  private recycle(e: RibbonEntry): void {
    this.ribbons.delete(e.key);
    e.gfx.park();
    vfxBudgetManager.onShapeDestroyed(true);
    const poolKey = `${e.style.depth}:${e.style.glow ? 1 : 0}`;
    const list = this.gfxPool.get(poolKey) ?? [];
    if (list.length < TRAIL_FX_CONFIG.maxActiveTrails) {
      list.push(e.gfx);
      this.gfxPool.set(poolKey, list);
    } else {
      e.gfx.destroy();
    }
    if (this.bufferPool.length < TRAIL_FX_CONFIG.maxActiveTrails) {
      this.bufferPool.push(e.buf);
    }
  }
}

/** Module singleton (CooldownStore pattern) — importable from rendering,
 *  the dash pipeline, and the effect player without cycles. */
export const TrailSystem = new TrailSystemImpl();
